import { motion } from "framer-motion";
import { ExternalLink, ShoppingBag, Heart, Shirt } from "lucide-react";

const Store = () => {
  return (
    <div data-testid="store-page">
      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-[#1A1C18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Shop With Purpose
            </span>
            <h1 className="font-['Bebas_Neue'] text-6xl lg:text-8xl text-white mb-6">
              Our Store
            </h1>
            <p className="text-xl text-[#A3A3A3] max-w-2xl mx-auto mb-12">
              Every purchase supports our rescue mission. 100% of proceeds go directly to saving animal lives.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Store Link Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="card-tactical p-12 text-center"
          >
            <div className="w-20 h-20 bg-[#4A5320] flex items-center justify-center mx-auto mb-8">
              <ShoppingBag className="w-10 h-10 text-[#FFD700]" />
            </div>
            <h2 className="font-['Bebas_Neue'] text-5xl text-white mb-6">
              Visit Our Online Store
            </h2>
            <p className="text-[#A3A3A3] text-lg mb-8 max-w-xl mx-auto">
              Browse our collection of apparel, accessories, and merchandise. Each item you purchase helps us continue our mission.
            </p>
            <a
              href="https://3eyeblindrescue.printful.me/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="visit-store-button"
              className="btn-primary inline-flex items-center gap-2 text-lg"
            >
              Shop Now
              <ExternalLink className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-24 bg-[#1A1C18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Merchandise
            </span>
            <h2 className="font-['Bebas_Neue'] text-5xl text-white">
              What We Offer
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shirt,
                title: "Apparel",
                description: "T-shirts, hoodies, and more featuring our rescue designs",
              },
              {
                icon: Heart,
                title: "Accessories",
                description: "Stickers, pins, and pet-themed goodies",
              },
              {
                icon: ShoppingBag,
                title: "Pet Supplies",
                description: "Quality items for your furry family members",
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 border border-[#4A5320] bg-[#0A0A0A]"
              >
                <div className="w-14 h-14 bg-[#4A5320] flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-7 h-7 text-[#FFD700]" />
                </div>
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-3">{item.title}</h3>
                <p className="text-[#A3A3A3] text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-[#4A5320]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Bebas_Neue'] text-4xl text-white mb-6">
            Prefer to Donate Directly?
          </h2>
          <p className="text-[#A3A3A3] text-lg mb-8">
            Every dollar counts. If you&apos;d rather donate directly to our rescue efforts, we make it easy.
          </p>
          <a
            href="/donate"
            data-testid="store-donate-link"
            className="btn-secondary inline-flex items-center gap-2"
          >
            Donate Now
          </a>
        </div>
      </section>
    </div>
  );
};

export default Store;
